import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { useApiService } from '../../services/api';
import { 
  DollarSign, 
  TrendingUp, 
  TrendingDown,
  Download,
  FileText,
  Search,
  Filter,
  RefreshCw
} from 'lucide-react';
import './FinancialSummary.css';

const FinancialSummary = forwardRef((props, ref) => {
  const apiService = useApiService();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [financialData, setFinancialData] = useState(null);
  const [showroomDetails, setShowroomDetails] = useState(null);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    customerId: ''
  });
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadFinancialData();
  }, [filters.startDate, filters.endDate, filters.customerId]);

  useEffect(() => {
    const loadShowroomDetails = async () => {
      try {
        const data = await apiService.getUserShowroom();
        setShowroomDetails(data || {});
      } catch (err) {
        console.error('Error loading showroom details:', err);
      }
    };
    loadShowroomDetails();
    // Run once on mount to avoid repeated calls
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ensureShowroomDetails = async () => {
    if (showroomDetails) return showroomDetails;
    try {
      const data = await apiService.getUserShowroom();
      setShowroomDetails(data || {});
      return data || {};
    } catch (err) {
      console.error('Error loading showroom details:', err);
      return {};
    }
  };

  const loadFinancialData = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.customerId) params.customerId = filters.customerId;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;

      const response = await apiService.getProfitLossReport(params);
      setFinancialData(response);
      setError(null);
    } catch (error) {
      console.error('Error loading financial data:', error);
      setError('Failed to load financial data');
    } finally {
      setLoading(false);
    }
  };

  const getFilteredData = () => {
    if (!financialData || !financialData.profitLossData) return [];
    
    let filtered = financialData.profitLossData;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(deal => 
        deal.dealId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        deal.purchaseCustomer?.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        deal.saleCustomer?.fullName?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filtered;
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return 'Rs. 0';
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (number) => {
    return new Intl.NumberFormat('en-PK').format(number || 0);
  };

  const getProfitColor = (amount) => {
    if (!amount || amount === 0) return '#6b7280';
    return amount > 0 ? '#10b981' : '#ef4444';
  };

  const buildShowroomHeader = (details = {}) => {
    const name = details.showroomName || details.name || 'Showroom';
    const contact = details.showroomPhone || details.contact || 'N/A';
    const email = details.showroomEmail || details.email || 'N/A';
    const address = details.showroomAddress || details.address || 'N/A';
    const initials = name
      .split(' ')
      .filter(Boolean)
      .map(part => part[0])
      .join('')
      .slice(0, 2)
      .toUpperCase() || 'SM';

    return `
      <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 8px;">
        <div style="width: 56px; height: 56px; border-radius: 10px; background-color: #eef2ff; color: #4338ca; display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 16px; border: 1px solid #e5e7eb;">
          ${initials}
        </div>
        <div style="text-align: left;">
          <div style="font-size: 18px; font-weight: 700; margin-bottom: 2px;">${name}</div>
          <div style="font-size: 11px; color: #6b7280; margin-bottom: 2px;">Address: ${address}</div>
          <div style="font-size: 11px; color: #6b7280; margin-bottom: 2px;">Contact: ${contact}</div>
          <div style="font-size: 11px; color: #6b7280;">Email: ${email}</div>
        </div>
      </div>
    `;
  };

  const calculateTotals = () => {
    const filteredData = getFilteredData();
    const soldDeals = filteredData.filter(deal => deal.saleValue);

    const baseTotals = filteredData.reduce((acc, deal) => {
      acc.totalPurchaseValue += deal.purchaseValue || 0;
      acc.totalSaleValue += deal.saleValue || 0;
      acc.totalExpenses += deal.expenses || 0;
      acc.totalGrossProfit += deal.grossProfit || 0;
      return acc;
    }, { 
      totalPurchaseValue: 0, 
      totalSaleValue: 0, 
      totalExpenses: 0, 
      totalGrossProfit: 0, 
      totalNetProfit: 0 
    });

    baseTotals.totalNetProfit = soldDeals.reduce((acc, deal) => acc + (deal.netProfit || 0), 0);

    return baseTotals;
  };

  const totals = calculateTotals();

  // Export functionality
  const exportToCSV = () => {
    const filteredData = getFilteredData();
    
    if (filteredData.length === 0) {
      alert('No data to export');
      return;
    }

    // Create CSV headers
    const headers = [
      'Deal ID',
      'Purchase Price',
      'Sell Price',
      'Expenses',
      'Gross Profit/Loss',
      'Net Profit/Loss',
      'Purchase Customer',
      'Sale Customer',
      'Deal Date'
    ];

    // Create CSV rows
    const csvRows = [
      headers.join(','),
      ...filteredData.map(deal => [
        `"${deal.dealId || 'N/A'}"`,
        deal.purchaseValue || 0,
        deal.saleValue || 0,
        deal.expenses || 0,
        deal.grossProfit || 0,
        deal.netProfit || 0,
        `"${deal.purchaseCustomer?.fullName || 'N/A'}"`,
        `"${deal.saleCustomer?.fullName || 'N/A'}"`,
        `"${deal.dealDate ? new Date(deal.dealDate).toLocaleDateString() : 'N/A'}"`
      ].join(','))
    ];

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `financial_summary_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // PDF Export functionality
  const exportToPDF = async () => {
    const filteredData = getFilteredData();
    
    if (filteredData.length === 0) {
      alert('No data to export');
      return;
    }

    try {
      const headerShowroom = await ensureShowroomDetails();
      // Dynamic import for jsPDF and html2canvas
      const { default: jsPDF } = await import('jspdf');
      const html2canvas = await import('html2canvas');

      // Create a temporary div for the PDF content
      const pdfContent = document.createElement('div');
      pdfContent.style.position = 'absolute';
      pdfContent.style.left = '-9999px';
      pdfContent.style.top = '0';
      pdfContent.style.width = '1200px';
      pdfContent.style.padding = '20px';
      pdfContent.style.fontFamily = 'Arial, sans-serif';
      pdfContent.style.backgroundColor = 'white';
      pdfContent.style.color = 'black';

      pdfContent.innerHTML = `
        <div style="text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 20px;">
          ${buildShowroomHeader(headerShowroom || {})}
          <div style="font-size: 24px; font-weight: bold; margin-top: 6px; margin-bottom: 5px;">Financial Summary Report</div>
          <div style="font-size: 12px; color: #666; margin-bottom: 5px;">Generated on: ${new Date().toLocaleString('en-PK')}</div>
        </div>
        
        <div style="margin-bottom: 20px;">
          <h3 style="margin-top: 0;">Summary</h3>
          <div style="margin-bottom: 10px;"><strong>Total Purchase Value:</strong> ${formatCurrency(totals.totalPurchaseValue)}</div>
          <div style="margin-bottom: 10px;"><strong>Total Sale Value:</strong> ${formatCurrency(totals.totalSaleValue)}</div>
          <div style="margin-bottom: 10px;"><strong>Total Expenses:</strong> ${formatCurrency(totals.totalExpenses)}</div>
          <div style="margin-bottom: 10px;"><strong>Total Gross Profit:</strong> ${formatCurrency(totals.totalGrossProfit)}</div>
          <div style="margin-bottom: 10px;"><strong>Total Net Profit:</strong> ${formatCurrency(totals.totalNetProfit)}</div>
        </div>
        
        <table style="width: 100%; border-collapse: collapse; margin-top: 20px;">
          <thead>
            <tr style="background-color: #f2f2f2;">
              <th style="border: 1px solid #ddd; padding: 8px; text-align: left; font-weight: bold;">Deal ID</th>
              <th style="border: 1px solid #ddd; padding: 8px; text-align: left; font-weight: bold;">Purchase Price</th>
              <th style="border: 1px solid #ddd; padding: 8px; text-align: left; font-weight: bold;">Sell Price</th>
              <th style="border: 1px solid #ddd; padding: 8px; text-align: left; font-weight: bold;">Expenses</th>
              <th style="border: 1px solid #ddd; padding: 8px; text-align: left; font-weight: bold;">Gross Profit/Loss</th>
              <th style="border: 1px solid #ddd; padding: 8px; text-align: left; font-weight: bold;">Net Profit/Loss</th>
              <th style="border: 1px solid #ddd; padding: 8px; text-align: left; font-weight: bold;">Purchase Customer</th>
              <th style="border: 1px solid #ddd; padding: 8px; text-align: left; font-weight: bold;">Sale Customer</th>
            </tr>
          </thead>
          <tbody>
            ${filteredData.map(deal => `
              <tr>
                <td style="border: 1px solid #ddd; padding: 8px; text-align: left;">${deal.dealId || 'N/A'}</td>
                <td style="border: 1px solid #ddd; padding: 8px; text-align: left;">${formatCurrency(deal.purchaseValue)}</td>
                <td style="border: 1px solid #ddd; padding: 8px; text-align: left;">${formatCurrency(deal.saleValue)}</td>
                <td style="border: 1px solid #ddd; padding: 8px; text-align: left;">${formatCurrency(deal.expenses)}</td>
                <td style="border: 1px solid #ddd; padding: 8px; text-align: left;">${formatCurrency(deal.grossProfit)}</td>
                <td style="border: 1px solid #ddd; padding: 8px; text-align: left;">${formatCurrency(deal.netProfit)}</td>
                <td style="border: 1px solid #ddd; padding: 8px; text-align: left;">${deal.purchaseCustomer?.fullName || 'N/A'}</td>
                <td style="border: 1px solid #ddd; padding: 8px; text-align: left;">${deal.saleCustomer?.fullName || 'N/A'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      `;

      document.body.appendChild(pdfContent);

      // Convert to canvas
      const canvas = await html2canvas.default(pdfContent, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff'
      });

      // Remove the temporary element
      document.body.removeChild(pdfContent);

      // Create PDF
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('l', 'mm', 'a4');
      const imgWidth = 297;
      const pageHeight = 210;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;

      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Add footer to all pages
      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(100, 100, 100);
        pdf.text('powered by ledger Glide | +923332416545', imgWidth - 10, pageHeight - 5, { align: 'right' });
      }

      // Save the PDF
      pdf.save(`financial_summary_${new Date().toISOString().split('T')[0]}.pdf`);

    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  // Expose export functions to parent component
  useImperativeHandle(ref, () => ({
    exportToCSV,
    exportToPDF
  }));

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '4rem', color: '#6b7280' }}>
        <div style={{ width: '40px', height: '40px', border: '4px solid #e5e7eb', borderTop: '4px solid #6366f1', borderRadius: '50%', animation: 'spin 1s linear infinite', marginBottom: '1rem' }}></div>
        <p>Loading financial data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '4rem', color: '#dc2626' }}>
        <p>{error}</p>
        <button onClick={loadFinancialData} style={{ marginTop: '1rem', padding: '0.75rem 1.5rem', background: '#dc2626', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '500', cursor: 'pointer' }}>
          Retry
        </button>
      </div>
    );
  }

  if (!financialData) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '4rem', color: '#dc2626' }}>
        <p>No financial data available</p>
        <button onClick={loadFinancialData} style={{ marginTop: '1rem', padding: '0.75rem 1.5rem', background: '#dc2626', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '500', cursor: 'pointer' }}>
          Load Data
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Header Section */}
      {/* <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1.5rem' }}>
        <div>
          <h2 style={{ color: '#1f2937', fontSize: '1.5rem', letterSpacing: '-0.025em', margin: 0 }}>Financial Summary</h2>
          <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: '0.5rem 0 0 0' }}>Deal-level profit/loss analysis and financial performance</p>
        </div>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <button className="btn d-flex align-items-center justify-content-center gap-2 px-3 px-md-4 py-2 fw-semibold" style={{ background: '#6366f1', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '0.875rem', boxShadow: '0 1px 3px 0 rgba(99, 102, 241, 0.1), 0 1px 2px 0 rgba(99, 102, 241, 0.06)' }} onClick={exportToCSV}>
            <Download size={16} />
            <span className="d-none d-sm-inline">Export CSV</span>
          </button>
          <button className="btn d-flex align-items-center justify-content-center gap-2 px-3 px-md-4 py-2 fw-semibold" style={{ background: '#dc2626', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '0.875rem', boxShadow: '0 1px 3px 0 rgba(220, 38, 38, 0.1), 0 1px 2px 0 rgba(220, 38, 38, 0.06)' }} onClick={exportToPDF}>
            <FileText size={16} />
            <span className="d-none d-sm-inline">Export PDF</span>
          </button>
        </div>
      </div> */}

      {/* Stats Cards */}
      <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
        <div style={{ flex: '1 1 180px', minWidth: 0 }}>
          <div className="card border-0 shadow-sm h-100" style={{ background: '#fff', borderRadius: '12px', width: '100%' }}>
            <div className="card-body p-3 p-md-4">
              <div className="d-flex align-items-center">
                <div style={{ width: 40, height: 40, background: '#f0f9ff', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <DollarSign size={20} style={{ color: '#0284c7' }} />
                </div>
                <div style={{ marginLeft: 12 }}>
                  <h6 className="mb-1 fw-semibold" style={{ color: '#1f2937', fontSize: '0.875rem' }}>Total Purchase Value</h6>
                  <p className="mb-0 text-muted" style={{ fontSize: '0.75rem' }}>{formatCurrency(totals.totalPurchaseValue)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div style={{ flex: '1 1 180px', minWidth: 0 }}>
          <div className="card border-0 shadow-sm h-100" style={{ background: '#fff', borderRadius: '12px', width: '100%' }}>
            <div className="card-body p-3 p-md-4">
              <div className="d-flex align-items-center">
                <div style={{ width: 40, height: 40, background: '#f0f9ff', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <DollarSign size={20} style={{ color: '#0284c7' }} />
                </div>
                <div style={{ marginLeft: 12 }}>
                  <h6 className="mb-1 fw-semibold" style={{ color: '#1f2937', fontSize: '0.875rem' }}>Total Sale Value</h6>
                  <p className="mb-0 text-muted" style={{ fontSize: '0.75rem' }}>{formatCurrency(totals.totalSaleValue)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div style={{ flex: '1 1 180px', minWidth: 0 }}>
          <div className="card border-0 shadow-sm h-100" style={{ background: '#fff', borderRadius: '12px', width: '100%' }}>
            <div className="card-body p-3 p-md-4">
              <div className="d-flex align-items-center">
                <div style={{ width: 40, height: 40, background: '#fef2f2', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <DollarSign size={20} style={{ color: '#dc2626' }} />
                </div>
                <div style={{ marginLeft: 12 }}>
                  <h6 className="mb-1 fw-semibold" style={{ color: '#1f2937', fontSize: '0.875rem' }}>Total Expenses</h6>
                  <p className="mb-0 text-muted" style={{ fontSize: '0.75rem' }}>{formatCurrency(totals.totalExpenses)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div style={{ flex: '1 1 180px', minWidth: 0 }}>
          <div className="card border-0 shadow-sm h-100" style={{ background: '#fff', borderRadius: '12px', width: '100%' }}>
            <div className="card-body p-3 p-md-4">
              <div className="d-flex align-items-center">
                <div style={{ width: 40, height: 40, background: totals.totalNetProfit >= 0 ? '#d1fae5' : '#fef3c7', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {totals.totalNetProfit >= 0 ? <TrendingUp size={20} style={{ color: '#065f46' }} /> : <TrendingDown size={20} style={{ color: '#92400e' }} />}
                </div>
                <div style={{ marginLeft: 12 }}>
                  <h6 className="mb-1 fw-semibold" style={{ color: '#1f2937', fontSize: '0.875rem' }}>Net Profit/Loss</h6>
                  <p className="mb-0 text-muted" style={{ fontSize: '0.75rem', color: getProfitColor(totals.totalNetProfit) }}>{formatCurrency(totals.totalNetProfit)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters Section */}
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ 
          display: 'flex', 
          gap: '1rem', 
          flexWrap: 'wrap', 
          alignItems: 'end',
          background: 'white',
          padding: '1.5rem',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          border: '1px solid #f3f4f6'
        }}>
          {/* Search */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', flex: '1 1 300px', minWidth: 0 }}>
            <label style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>Search</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', background: 'white', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0.75rem 1rem' }}>
              <Search size={20} style={{ color: '#6b7280' }} />
              <input
                type="text"
                placeholder="Search by deal ID or customer name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ border: 'none', outline: 'none', flex: 1, fontSize: '0.875rem' }}
              />
            </div>
          </div>

          {/* Date Filters */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', flex: '1 1 150px', minWidth: 0 }}>
            <label style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>Start Date</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
              style={{ 
                padding: '0.75rem', 
                border: '1px solid #d1d5db', 
                borderRadius: '8px', 
                fontSize: '0.875rem',
                background: 'white',
                width: '100%'
              }}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', flex: '1 1 150px', minWidth: 0 }}>
            <label style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>End Date</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
              style={{ 
                padding: '0.75rem', 
                border: '1px solid #d1d5db', 
                borderRadius: '8px', 
                fontSize: '0.875rem',
                background: 'white',
                width: '100%'
              }}
            />
          </div>

          {/* Clear Filters Button */}
          <div style={{ display: 'flex', alignItems: 'end', height: '100%' }}>
            <button
              onClick={() => {
                setFilters({
                  startDate: '',
                  endDate: '',
                  customerId: ''
                });
                setSearchTerm('');
              }}
              style={{ 
                padding: '0.75rem 1rem', 
                background: '#f8fafc', 
                color: '#64748b', 
                border: '1px solid #e2e8f0', 
                borderRadius: '8px', 
                fontSize: '0.875rem', 
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Financial Summary Table */}
      <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>Loading financial data...</div>
        ) : (
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1200px' }}>
            <thead>
              <tr style={{ background: '#f9fafb' }}>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>Deal ID</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>Purchase Price</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>Sell Price</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>Expenses</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>Gross Profit/Loss</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>Net Profit/Loss</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>Purchase Customer</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>Sale Customer</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>Deal Date</th>
              </tr>
            </thead>
            <tbody>
              {getFilteredData().map((deal, index) => (
                <tr key={index} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '1rem', color: '#1f2937', fontSize: '0.875rem', fontWeight: '600' }}>
                    {deal.dealId || 'N/A'}
                  </td>
                  <td style={{ padding: '1rem', color: '#1f2937', fontSize: '0.875rem' }}>
                    {formatCurrency(deal.purchaseValue)}
                  </td>
                  <td style={{ padding: '1rem', color: '#1f2937', fontSize: '0.875rem' }}>
                    {deal.saleValue ? formatCurrency(deal.saleValue) : '-'}
                  </td>
                  <td style={{ padding: '1rem', color: '#1f2937', fontSize: '0.875rem' }}>
                    {formatCurrency(deal.expenses)}
                  </td>
                  <td style={{ padding: '1rem' }}>
                    {deal.saleValue ? (
                      <span style={{ 
                        color: getProfitColor(deal.grossProfit),
                        fontWeight: '600',
                        fontSize: '0.875rem'
                      }}>
                        {formatCurrency(deal.grossProfit)}
                      </span>
                    ) : (
                      <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>-</span>
                    )}
                  </td>
                  <td style={{ padding: '1rem' }}>
                    {deal.saleValue ? (
                      <span style={{ 
                        color: getProfitColor(deal.netProfit),
                        fontWeight: '600',
                        fontSize: '0.875rem'
                      }}>
                        {formatCurrency(deal.netProfit)}
                      </span>
                    ) : (
                      <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>-</span>
                    )}
                  </td>
                  <td style={{ padding: '1rem', color: '#1f2937', fontSize: '0.875rem' }}>
                    {deal.purchaseCustomer?.fullName || 'N/A'}
                  </td>
                  <td style={{ padding: '1rem', color: '#1f2937', fontSize: '0.875rem' }}>
                    {deal.saleCustomer?.fullName || 'N/A'}
                  </td>
                  <td style={{ padding: '1rem', color: '#6b7280', fontSize: '0.875rem' }}>
                    {deal.dealDate ? new Date(deal.dealDate).toLocaleDateString() : 'N/A'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
});

export default FinancialSummary;
